import * as THREE from 'three';

export interface PlasmaPlumeOptions {
  /** White-hot tone (defaults to white). Can be tinted slightly per faction. */
  readonly colorHot?: number;
  /** Cool/saturated tone — the faction plasma color. */
  readonly colorCool: number;
  /** Outer baseline tube length, in world units. Everything else (halo,
   *  mid, core, streaks, particles) scales from this — matches the demo's
   *  outer cylinder length of 3.6 as the unit reference. */
  readonly length?: number;
  /** Outer baseline tube base radius. Defaults to demo's 0.46 ratio of
   *  length, kept for callers that need a wider/narrower flame. */
  readonly radius?: number;
  /** Idle baseline 0..1 — visibility at zero thrust. */
  readonly idleLevel?: number;
}

const tubeVertex = /* glsl */ `
  uniform float uTime;
  uniform float uLength;
  uniform float uSpeed;

  varying float vAlong;
  varying float vRadial;

  void main() {
    vec3 pos = position;

    // After geometry's rotateX(+PI/2) the cylinder lies along +Z, so
    // pos.z is the along-axis coord and (pos.x, pos.y) is the radial plane.
    float along = (pos.z / uLength) + 0.5;
    along = clamp(along, 0.0, 1.0);

    float taperInfluence = 1.0 - along;

    float waveA = sin((along * 10.0) - uTime * 2.6 * uSpeed) * 0.03;
    float waveB = sin((along * 18.0) - uTime * 4.1 * uSpeed) * 0.015;
    float waveC = sin((position.x * 7.0) + (position.y * 6.0) - uTime * 1.8 * uSpeed) * 0.01;

    vec2 radial = normalize(vec2(pos.x, pos.y) + 1e-5);
    float deform = (waveA + waveB + waveC) * taperInfluence;

    pos.x += radial.x * deform;
    pos.y += radial.y * deform;

    vAlong = along;
    vRadial = length(vec2(position.x, position.y));

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const tubeFragment = /* glsl */ `
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform vec3 uColorC;
  uniform float uOpacity;
  uniform float uRadiusFalloff;

  varying float vAlong;
  varying float vRadial;

  void main() {
    // Single GLOBAL falloff applied to every layer. The wide halo's base
    // sits OUTSIDE this radius (invisible) and only lights up as it tapers
    // toward the tip; the narrow core sits ENTIRELY inside it so its hot
    // spine bleeds through every wider tube. That cross-layer overlap is
    // what gives the plume its smooth, "filled" continuous look.
    float centerWeight = 1.0 - smoothstep(0.0, uRadiusFalloff, vRadial);
    float tipFade = 1.0 - smoothstep(0.78, 1.0, vAlong);
    float startFade = smoothstep(0.0, 0.06, vAlong);

    float intensity = centerWeight * tipFade * startFade;

    vec3 color = mix(uColorC, uColorB, centerWeight);
    color = mix(color, uColorA, pow(centerWeight, 2.4));

    float alpha = intensity * uOpacity;
    gl_FragColor = vec4(color, alpha);
  }
`;

const particleVertex = /* glsl */ `
  attribute float aSeed;
  uniform float uTime;
  uniform float uLength;
  uniform float uOrbitRadius;
  uniform float uIntensity;
  uniform float uPointMul;

  varying float vAlpha;

  void main() {
    vec3 pos = position;

    // Same life cycle as the reference demo: each particle has a unique
    // seed so they don't march in lockstep.
    float life = mod(uTime * (1.1 + fract(aSeed)) + aSeed, 4.0);
    pos.z = (life / 4.0) * uLength;

    float swirl = uTime * 1.3 + aSeed;
    float r = (0.02 + fract(aSeed * 3.17) * 0.12) * uOrbitRadius;
    pos.x = cos(swirl) * r;
    pos.y = sin(swirl) * r;

    float fade = 1.0 - smoothstep(2.8, 4.0, life);

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    float dist = max(0.5, -mv.z);

    // Demo formula: gl_PointSize = 4.5 * (1 / -mv.z). For our small ship
    // plumes (and chase camera at ~9u from the engine) we bump the
    // multiplier so particles register as soft sparkles instead of
    // sub-pixel noise. Cull entirely when wantedSize would still be below
    // ~1.2 px — that's the threshold where additive points read as
    // flickering hard squares on distant NPCs.
    float wantedSize = uPointMul / dist;
    float distFade = 1.0 - smoothstep(35.0, 70.0, dist);

    if (wantedSize < 1.2 || distFade <= 0.001) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vAlpha = 0.0;
      return;
    }

    vAlpha = fade * uIntensity * distFade;
    gl_PointSize = clamp(wantedSize, 1.2, 14.0);
    gl_Position = projectionMatrix * mv;
  }
`;

const particleFragment = /* glsl */ `
  varying float vAlpha;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv);
    float a = smoothstep(0.5, 0.0, d) * vAlpha;
    gl_FragColor = vec4(0.86, 0.95, 1.0, a);
  }
`;

const streakVertex = /* glsl */ `
  uniform float uMaxDist;

  varying vec3 vWorldPos;
  varying float vCulled;

  void main() {
    // Distance from the streak's own world origin to the camera. When the
    // streak is far away its triangles sub-pixel-rasterize into bright
    // additive specks that flicker frame-to-frame; collapse the entire
    // mesh to one off-screen point in that case so the GPU draws nothing.
    vec3 originWS = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    float originDist = length(cameraPosition - originWS);

    if (originDist > uMaxDist) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      vCulled = 1.0;
      vWorldPos = originWS;
      return;
    }

    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vCulled = 0.0;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const streakFragment = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uFadeStart;
  uniform float uFadeEnd;

  varying vec3 vWorldPos;
  varying float vCulled;

  void main() {
    if (vCulled > 0.5) discard;
    float dist = length(cameraPosition - vWorldPos);
    float fade = 1.0 - smoothstep(uFadeStart, uFadeEnd, dist);
    float a = uOpacity * fade;
    if (a < 0.005) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

interface PlumeLayer {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.ShaderMaterial;
  readonly baseOpacity: number;
  readonly breathSpeedX: number;
  readonly breathSpeedY: number;
  readonly breathAmp: number;
}

interface Streak {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.ShaderMaterial;
  readonly baseOpacity: number;
}

// Layer specs are LITERAL values from the reference plasma demo. Lengths
// and radii get multiplied by `scaleL` / `scaleR` at construction time so
// the same tuned proportions work at any plume size.
const LAYER_SPECS = [
  // halo
  {
    length: 4.0,
    radiusStart: 0.62,
    radiusEnd: 0.08,
    opacity: 0.08,
    speed: 0.75,
    hotMixA: 0.42,
    hotMixB: 0.22,
    hotMixC: -0.55,
    breathSpeedX: 4.5,
    breathSpeedY: 4.2,
    breathAmp: 0.01
  },
  // outer
  {
    length: 3.6,
    radiusStart: 0.46,
    radiusEnd: 0.05,
    opacity: 0.24,
    speed: 0.9,
    hotMixA: 0.62,
    hotMixB: 0.40,
    hotMixC: 0.0,
    breathSpeedX: 6.0,
    breathSpeedY: 5.4,
    breathAmp: 0.012
  },
  // mid
  {
    length: 3.0,
    radiusStart: 0.28,
    radiusEnd: 0.035,
    opacity: 0.42,
    speed: 1.0,
    hotMixA: 0.92,
    hotMixB: 0.78,
    hotMixC: 0.42,
    breathSpeedX: 7.0,
    breathSpeedY: 6.6,
    breathAmp: 0.016
  },
  // core
  {
    length: 2.4,
    radiusStart: 0.12,
    radiusEnd: 0.018,
    opacity: 0.9,
    speed: 1.12,
    hotMixA: 1.0,
    hotMixB: 0.92,
    hotMixC: 0.65,
    breathSpeedX: 8.4,
    breathSpeedY: 7.9,
    breathAmp: 0.02
  }
] as const;

/**
 * Layered plasma plume — direct port of the reference plasma demo, scaled
 * to fit ship engines. Four nested cylinder tubes (wide halo → white-hot
 * core), shader-driven wave deformation, an inner orbit of bright
 * particles, and a few drifting streaks for body. The whole group scales
 * along its axis with thrust so the plume grows as the engine spools up;
 * the idle baseline keeps a contained tongue of plasma glowing at the
 * throat. NPC-distance flicker is handled via a sub-pixel cull on the
 * particle shader and a vertex-stage cull on the streak shader.
 */
export class PlasmaPlume {
  readonly object3D: THREE.Group;

  private readonly layers: PlumeLayer[] = [];
  private readonly streaks: Streak[] = [];
  private readonly particlesMaterial: THREE.ShaderMaterial;
  private readonly particlesGeometry: THREE.BufferGeometry;
  private readonly particles: THREE.Points;
  private readonly idleLevel: number;
  private intensity: number;

  constructor(options: PlasmaPlumeOptions) {
    const baselineLength = options.length ?? 3.6;
    const baselineRadius = options.radius ?? baselineLength * (0.46 / 3.6);
    this.idleLevel = THREE.MathUtils.clamp(options.idleLevel ?? 0.34, 0, 1);
    this.intensity = this.idleLevel;

    // Two scale factors so callers can independently stretch length vs
    // radius, but in normal use both are the same. The demo's outer tube
    // is the "1.0" reference (length 3.6, radius 0.46).
    const scaleL = baselineLength / 3.6;
    const scaleR = baselineRadius / 0.46;

    const cool = options.colorCool;
    const white = options.colorHot ?? 0xffffff;

    this.object3D = new THREE.Group();
    this.object3D.name = 'plasma-plume';

    // Single GLOBAL falloff scaled with the radial size — lifted from the
    // demo's hardcoded 0.55, so the wide halo's base falls outside it
    // (invisible) while every inner layer sits inside (bright spine).
    const globalFalloff = 0.55 * scaleR;

    for (const spec of LAYER_SPECS) {
      const layerLength = spec.length * scaleL;
      const radiusBase = spec.radiusStart * scaleR;
      const radiusTip = spec.radiusEnd * scaleR;

      // CylinderGeometry(rTop, rBottom, h): rTop at +Y, rBottom at -Y.
      // We want the wide base at the engine and the thin tip extending
      // away, so rTop = tip and rBottom = base. After rotateX(+PI/2)
      // the +Y end maps to +Z, putting the tip at +Z and the base at -Z.
      // Mesh then translates by +length/2 in Z so the base sits exactly
      // at the engine mount and the tip at z = length.
      const geometry = new THREE.CylinderGeometry(
        radiusTip,
        radiusBase,
        layerLength,
        28,
        48,
        true
      );
      geometry.rotateX(Math.PI / 2);
      geometry.boundingSphere = new THREE.Sphere(
        new THREE.Vector3(),
        Infinity
      );

      const colorA = mixHotCool(cool, white, spec.hotMixA);
      const colorB = mixHotCool(cool, white, spec.hotMixB);
      const colorC = mixHotCool(cool, white, spec.hotMixC);

      const material = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        uniforms: {
          uTime: { value: 0 },
          uColorA: { value: new THREE.Color(colorA) },
          uColorB: { value: new THREE.Color(colorB) },
          uColorC: { value: new THREE.Color(colorC) },
          uOpacity: { value: spec.opacity },
          uLength: { value: layerLength },
          uSpeed: { value: spec.speed },
          uRadiusFalloff: { value: globalFalloff }
        },
        vertexShader: tubeVertex,
        fragmentShader: tubeFragment
      });
      material.toneMapped = false;

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.z = layerLength / 2;
      mesh.frustumCulled = false;
      mesh.renderOrder = 5;

      this.object3D.add(mesh);
      this.layers.push({
        mesh,
        material,
        baseOpacity: spec.opacity,
        breathSpeedX: spec.breathSpeedX,
        breathSpeedY: spec.breathSpeedY,
        breathAmp: spec.breathAmp
      });
    }

    // Streaks — literal port of the reference demo (10 of them, length
    // 1.5..2.8, position x 1.0..1.9, orbit radius 0..0.12), all scaled.
    // These proportions guarantee every streak's full extent stays inside
    // the plume envelope so they never poke past the tip as random sticks.
    const streakCount = 10;
    for (let i = 0; i < streakCount; i++) {
      const streakLength = scaleL * (1.5 + Math.random() * 1.3);
      const geo = new THREE.CylinderGeometry(
        scaleR * 0.008,
        scaleR * 0.003,
        streakLength,
        8,
        1,
        true
      );
      geo.rotateX(Math.PI / 2);
      geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

      // Demo uses two slightly different bright tints (0xdff2ff / 0x89c2ff).
      // We approximate by mixing the faction's cool toward white at two
      // different strengths so the streaks pick up the faction palette.
      const baseColor =
        i % 2 === 0
          ? mixHotCool(cool, white, 0.92)
          : mixHotCool(cool, white, 0.65);
      const material = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        uniforms: {
          uColor: { value: new THREE.Color(baseColor) },
          uOpacity: { value: 0.12 },
          uMaxDist: { value: 50.0 },
          uFadeStart: { value: 28.0 },
          uFadeEnd: { value: 50.0 }
        },
        vertexShader: streakVertex,
        fragmentShader: streakFragment
      });
      material.toneMapped = false;

      const angle = Math.random() * Math.PI * 2;
      const r = Math.random() * scaleR * 0.12;
      const mesh = new THREE.Mesh(geo, material);
      mesh.position.set(
        Math.cos(angle) * r,
        Math.sin(angle) * r,
        scaleL * (1.0 + Math.random() * 0.9)
      );
      mesh.rotation.x = (Math.random() - 0.5) * 0.15;
      mesh.rotation.y = (Math.random() - 0.5) * 0.15;
      mesh.frustumCulled = false;
      mesh.renderOrder = 5;

      this.object3D.add(mesh);
      this.streaks.push({ mesh, material, baseOpacity: 0.12 });
    }

    // Particles — literal port of the demo (140 of them, life cycle 0..4,
    // scrolling along the axis). Vertex shader rebuilds positions every
    // frame from the seed attribute, so the buffer values are placeholders.
    const particleCount = 140;
    const positions = new Float32Array(particleCount * 3);
    const seeds = new Float32Array(particleCount);
    for (let i = 0; i < particleCount; i++) {
      seeds[i] = Math.random() * 100;
    }

    this.particlesGeometry = new THREE.BufferGeometry();
    this.particlesGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3)
    );
    this.particlesGeometry.setAttribute(
      'aSeed',
      new THREE.BufferAttribute(seeds, 1)
    );
    this.particlesGeometry.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(),
      Infinity
    );

    // Outer baseline length defines how far the particles travel along
    // the plume — matches the demo (life * 1 with their plume length 3.6).
    const particleAxialLength = 3.6 * scaleL;
    this.particlesMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uLength: { value: particleAxialLength },
        // 1.0 in the demo (the swirl radius coefficient is hardcoded
        // 0..0.14). We scale by scaleR so particles orbit inside the
        // plume regardless of size.
        uOrbitRadius: { value: scaleR },
        uIntensity: { value: this.intensity },
        // Demo uses 4.5; bumped so particles register as visible sparkles
        // through the chase camera (~9u from the player engine) instead
        // of falling under the sub-pixel cull.
        uPointMul: { value: 14.0 }
      },
      vertexShader: particleVertex,
      fragmentShader: particleFragment
    });
    this.particlesMaterial.toneMapped = false;

    this.particles = new THREE.Points(
      this.particlesGeometry,
      this.particlesMaterial
    );
    this.particles.frustumCulled = false;
    this.particles.renderOrder = 6;
    this.object3D.add(this.particles);
  }

  /** Drive the plume from a thrust value 0..1. The plume always shows the
   *  idle baseline so the engine throat reads as hot at rest. */
  setThrust(thrust: number): void {
    const clamped = THREE.MathUtils.clamp(thrust, 0, 1);
    this.intensity = THREE.MathUtils.lerp(this.idleLevel, 1, clamped);
  }

  /** Push the time-dependent uniforms — call every frame the ship updates. */
  tick(time: number): void {
    const intensity = this.intensity;

    // Length scales with intensity along the engine axis only — the
    // radial dimensions stay fixed so the plume grows longer rather than
    // fatter.
    const lengthScale = 0.18 + 0.82 * intensity;
    this.object3D.scale.set(1, 1, lengthScale);

    const opacityScale = 0.5 + 0.5 * intensity;

    for (const layer of this.layers) {
      const u = layer.material.uniforms;
      if (u['uTime']) u['uTime'].value = time;
      if (u['uOpacity']) u['uOpacity'].value = layer.baseOpacity * opacityScale;

      const bx = 1 + Math.sin(time * layer.breathSpeedX) * layer.breathAmp;
      const by = 1 + Math.sin(time * layer.breathSpeedY) * layer.breathAmp;
      layer.mesh.scale.set(bx, by, 1);
    }

    for (let i = 0; i < this.streaks.length; i++) {
      const streak = this.streaks[i];
      if (!streak) continue;
      const pulse = 0.66 + 0.16 * Math.sin(time * 6 + i * 0.8);
      const u = streak.material.uniforms;
      if (u['uOpacity'])
        u['uOpacity'].value = streak.baseOpacity * pulse * opacityScale;
    }

    const pu = this.particlesMaterial.uniforms;
    if (pu['uTime']) pu['uTime'].value = time;
    if (pu['uIntensity']) pu['uIntensity'].value = intensity;
  }

  dispose(): void {
    for (const layer of this.layers) {
      layer.mesh.geometry.dispose();
      layer.material.dispose();
    }
    for (const streak of this.streaks) {
      streak.mesh.geometry.dispose();
      streak.material.dispose();
    }
    this.particlesGeometry.dispose();
    this.particlesMaterial.dispose();
  }
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

function shadeDown(hex: number, factor: number): number {
  const r = Math.round(((hex >> 16) & 0xff) * factor);
  const g = Math.round(((hex >> 8) & 0xff) * factor);
  const b = Math.round((hex & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

/**
 * Blend a "cool" base color toward a "white-hot" highlight color, with
 * the mix factor in [-1, 1]:
 *   -1 → fully shaded-down cool (deep saturated, used for halo's outer edge)
 *    0 → exactly the cool color
 *   +1 → exactly the white-hot color
 * Negative values darken the cool tone while preserving its hue, which is
 * what lets the wide halo end on a richly saturated outer rim instead of
 * fading to gray.
 */
function mixHotCool(cool: number, white: number, mix: number): number {
  if (mix >= 0) return lerpColor(cool, white, Math.min(mix, 1));
  return shadeDown(cool, 1 + Math.max(mix, -1));
}
